// Lenny Face collection — text emoticons built from the combining "Lenny" eyes
// ( ͡° ͜ʖ ͡°) and friends. Each entry is a whole string copied verbatim (like the
// /decorative combos), not a single Unicode codepoint, so it lives outside the
// /categories taxonomy. Built with explicit \u escapes for the combining marks
// because those glyphs are invisible/fragile when typed directly.

const BA = "͡"; // ͡  COMBINING DOUBLE INVERTED BREVE (the "eyebrow")
const BB = "͜"; // ͜  COMBINING DOUBLE BREVE BELOW (the "smile")
const LZ = "ʖ"; // ʖ  LATIN LETTER LEZH (the "mouth")

/** Build a symmetric Lenny face with the given eye glyph. */
const lenny = (eye: string): string => `( ${BA}${eye} ${BB}${LZ} ${BA}${eye})`;

// A spread of eye glyphs — each yields a distinct face.
const EYES = [
  "°", // °
  "◉", // ◉
  "⊙", // ⊙
  "•", // •
  "ᵔ", // ᵔ
  "ʘ", // ʘ
  "≖", // ≖
  "■", // ■
  "☉", // ☉
  "✧", // ✧
  "♥", // ♥
  "ಠ", // ಠ
  "ಥ", // ಥ
  "⚆", // ⚆
  "↑", // ↑
  "$",
  "◔", // ◔
  "ↀ", // ↀ
  "◕", // ◕
  "◑", // ◑
  "◐", // ◐
  "✪", // ✪
  "●", // ●
  "¬", // ¬
];

const CLASSIC = lenny("°"); // ( ͡° ͜ʖ ͡°)

// Lenny with arms / actions, plus a few one-off shapes.
const SPECIALS = [
  CLASSIC + "╯", // ╯  flip
  CLASSIC + "つ", // つ  point
  "¯\\_" + CLASSIC + "_/¯", // ¯\_( ͡° ͜ʖ ͡°)_/¯  shrug
  "ヽ" + CLASSIC + "ﾉ", // ヽ...ﾉ  cheer
  "╰" + CLASSIC + "つ", // ╰...つ  offering
  CLASSIC + "/",
  "ᕦ" + CLASSIC + "ᕤ", // ᕦ...ᕤ  flex
  "ᕙ" + CLASSIC + "ᕗ", // ᕙ...ᕗ  flex 2
  "ᖕ" + CLASSIC + "ᕗ", // ᖕ...ᕗ  strong
  CLASSIC + "╭∩╮", // ╭∩╮  rude
  CLASSIC + " ︻╦╤─", // ︻╦╤─  gun
  "╰" + CLASSIC + "つ──☆", // ╰...つ──☆  magic
  // sunglasses: ( ͡⌐■ ͜ʖ ͡■)
  `( ${BA}⌐■ ${BB}${LZ} ${BA}■)`,
  // bear lenny: ʕ ͡° ʖ̯ ͡°ʔ
  `ʕ ${BA}° ${LZ}̯ ${BA}°ʔ`,
  // peeking: ┬┴┬┴┤( ͡° ͜ʖ├┬┴┬┴
  `┬┴┬┴┤( ${BA}° ${BB}${LZ}├┬┴┬┴`,
  // faceless: ( ͡ ͜ʖ ͡)
  `( ${BA} ${BB}${LZ} ${BA})`,
  // raised eyebrow: ( ͡ ͡° ͜ʖ ͡°)
  `( ${BA} ${BA}° ${BB}${LZ} ${BA}°)`,
  // wide: ( ͡° ͜ʖ ͡ °)
  `( ${BA}° ${BB}${LZ} ${BA} °)`,
];

export const LENNY_FACES: string[] = [...EYES.map(lenny), ...SPECIALS];

export const LENNY_META = {
  name: "Lenny Face",
  hero: CLASSIC,
  tagline:
    "Copy and paste Lenny Face ( ͡° ͜ʖ ͡°) and dozens of variations — winking, sparkly, shrugging, flipping and more.",
  intro: [
    "Lenny Face is the text emoticon ( ͡° ͜ʖ ͡°), built from combining characters that stack a raised eyebrow and a sly smile onto a pair of eyes. Because it is plain text — not an image or a single emoji — it pastes anywhere you can type: chats, comments, bios, usernames and captions.",
    "Below is a big set of ready-made Lenny Faces: swap the eyes for stars, hearts, money or shades, or grab one already holding a gun, throwing a shrug, peeking around a corner or casting a little magic. Tap any face to copy the whole thing.",
  ],
} as const;
